import { useCallback } from 'react';
import { useAppDispatch } from '@store/hooks';

import { patientsApi } from '../store/api';

/**
 * Invalidate the RTK Query cache for a patient after a write that bypasses
 * RTK Query (the legacy `POST /patient/update` via APIService).
 *
 * Surfaces that read patient data through `usePatientInfoQuery` /
 * `usePatientClinicalInfoQuery` (Send Forms modal, EncounterViewV2, context
 * bar) only refresh via tag invalidation. The V2 mutations invalidate these
 * tags automatically; legacy call sites must do it explicitly on success or
 * those surfaces keep serving a stale snapshot (GDN-2202).
 *
 * Prefer `useUpdatePatientMutation` for new code — this hook exists to patch
 * the legacy write paths until they migrate.
 */
export const useInvalidatePatientCache = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    (patientId: string) => {
      if (!patientId) return;
      // Same tag set as the V2 patient PATCH (patchV2ProviderPatientsByPatientId).
      dispatch(
        patientsApi.util.invalidateTags([
          { type: 'PatientInfo', id: patientId },
          { type: 'PatientClinicalInfo', id: patientId },
        ]),
      );
    },
    [dispatch],
  );
};
